import {
  memo,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconFileCertificate } from '@tabler/icons-react';
import { Widget } from '@typeform/embed-react';
import { IconCircleCheckDisabled, IconCircleDisabled } from 'icons';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { ContentStep, PlanAssignment } from '@cfa/pathway-fe-shared/xp';
import { LoadingIndicator } from '@cfa/react-core';

import { getStepQuizId, getStepQuizV2 } from '@/utils/contentStep';
import { extractStatusCode } from '@/utils/error-utils';
import { cn } from '@/utils/tailwind';

import ConfirmationModal from 'components/popups/ConfirmationModal';
import PathwayModal from 'components/popups/PathwayModal';
import Constants from 'constants/index';
import { itemByLanguage } from 'utils/language';

/**
 * Refetch result shape: { error?: unknown } signals a refetch error (e.g., heartbeat 404);
 * undefined signals success (no error).
 */
type PlanQuizRefetchResult = { error?: unknown } | undefined;

/**
 * Callback that refetches quiz status and returns result or error.
 * - Return { error: ... } to signal a refetch failure (polling will check error code).
 * - Return undefined to signal success (no error, continue polling normally).
 * - Return void or Promise<void> for no-op handlers (read-only/disabled states).
 */
type PlanQuizRefetchWithResult = () =>
  | PlanQuizRefetchResult
  | Promise<PlanQuizRefetchResult>;
type PlanQuizRefetchNoop = (() => void) | (() => Promise<void>);
type PlanQuizRefetch = PlanQuizRefetchWithResult | PlanQuizRefetchNoop;

interface PlanItemQuizProps {
  disabled?: boolean;
  isActive: boolean;
  isComplete: boolean;
  language: string;
  onClick: (step: ContentStep) => void;
  onRefetch: PlanQuizRefetch;
  planId: string;
  statuses: PlanAssignment[];
  step: ContentStep;
  users: string[];
}

/**
 * Inline Typeform quiz step for a training plan card.
 *
 * ## V1 / V2 branch
 *
 * Branch on `isQuizV2` (`getStepQuizV2(step)` — plan-step `quizV2`, not the LD flag).
 *
 * - **V2:** Render the Typeform `Widget` with snake_case hidden fields
 *   (`plan_id`, `step_id`, `user_ids`) and `tracking: { 'typeform-lang': 'en' | 'es' }`.
 * - **V1:** Same Widget with classic camelCase hidden fields
 *   (`userIds`, `checklistId`, `stepId`, `language`). Pass / fail / wait UX stays on
 *   the existing PathwayModal path (`isLoading`, pass/fail popups, delayed/issue).
 *
 * ## V2 submit → poll for results
 *
 * `Widget.onSubmit` → `handleQuizSubmit()` starts polling (`pollingCount = 1`) and sets
 * `v2PollPhase` to `'polling'` (loading overlay over the iframe). Typeform’s webhook
 * tells XP the quiz finished. We poll via `onRefetch` and look in the assignments /
 * step statuses for the current `stepId`. We watch `eventId`: XP regenerates it when
 * the quiz result updates. On each cycle:
 *
 * ```
 * const eventIdsAreSame = currentStepEventId === lastPolledEventId;
 * ```
 *
 * If `eventId` changed, stop polling, clear the loader, and process the result.
 * If `eventId` is unchanged (or missing), wait 2s and call `pollQuizResult()` again.
 * Cap is `maxPollingCount` (7 ≈ 14s); exhaustion (or heartbeat 404) opens the
 * results-issue modal — V2 never uses the delayed Wait path.
 *
 * A recognized-transient refetch failure (status 0, 429, 5xx, or no status at all —
 * see `isTransientRefetchStatus`) does not spend `pollingCount`: it retries on its own
 * `maxTransientFailureCount` budget (`hasTransientRetryBudget` /
 * `retryAfterTransientFailure`) so a short network blip can't exhaust the main poll cap
 * before a delayed success lands (PATHWAY-3781). Worst case before the results-issue /
 * delayed modal now appears is `maxTransientFailureCount + maxPollingCount` cycles
 * (~34s), not the ~14s above. Any other non-404 failure with a *defined*, non-transient
 * status (e.g. a permanent 401/403) skips that budget and spends `pollingCount`
 * immediately, same as before PATHWAY-3781.
 *
 * ## Process the result (V2)
 *
 * - **Pass:** Close the Widget (`releaseWidgetHold` / drop `keepWidgetOpen`) and show
 *   the green `PassMessage` (“Passed”, or the scored congratulations string). A late
 *   `isComplete` also clears any issue modal and stops an in-flight poll.
 * - **Fail:** Set `v2PollPhase` to `'failed'` (overlay off; Typeform ending stays up).
 *   The form’s ending button → `onEndingButtonClick` → `reloadQuiz()`, which resets
 *   poll state and bumps `quizKey` so the Widget remounts.
 *
 * ## Shared guards
 *
 * - `keepWidgetOpen` latches the Widget open while V2 polls so parent `isActive` /
 *   `isComplete` flips from refetch do not unmount Typeform mid-result.
 * - `pollGenerationRef` invalidates in-flight `pollQuizResult` callbacks after
 *   `stopPolling` / remount so a late refetch cannot restart the loop.
 * - `showQuizWidget = (isActive && !isComplete) || keepWidgetOpen`.
 */
const PlanItemQuiz: React.FC<PlanItemQuizProps> = ({
  disabled,
  isActive,
  isComplete,
  language,
  onClick,
  onRefetch,
  planId,
  statuses,
  step,
  users,
}) => {
  const { t } = useTranslation();
  const isQuizV2 = getStepQuizV2(step);
  const typeformLang =
    language === Constants.LANGUAGE.SPANISH_LANGUAGE_CODE ? 'es' : 'en';
  const [lastPolledEventId, setLastPolledEventId] = useState('');
  const [activeQuizScore, setActiveQuizScore] = useState<number | null>(null);
  const [showPassPopup, setShowPassPopup] = useState(false);
  const [showFailPopup, setShowFailPopup] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);
  const [pollingRetry, setPollingRetry] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuizResultsDelayed, setShowQuizResultsDelayed] = useState(false);
  const [showQuizResultsIssue, setShowQuizResultsIssue] = useState(false);
  const [quizKey, setQuizKey] = useState<string>();
  // V2: local latch so parent isComplete / isActive flips from refetch do not unmount Typeform.
  const [keepWidgetOpen, setKeepWidgetOpen] = useState(false);
  const [v2PollPhase, setV2PollPhase] = useState<'idle' | 'polling' | 'failed'>(
    'idle',
  );
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  // Generation bumps on stop so in-flight poll callbacks cannot restart the loop.
  const pollGenerationRef = useRef(0);
  // Consecutive transient (non-404) refetch failures since the last success or reset.
  const transientFailureCountRef = useRef(0);

  const maxPollingCount = 7; // we poll every 2 seconds, this number would correspond to the time spent polling. 14 seconds at 7
  // Recognized-transient blips (status 0/429/5xx, or no status at all) retry on their
  // own budget so they don't burn maxPollingCount (PATHWAY-3781: a burst of network
  // errors was exhausting polling before a delayed success could land). Worst case adds
  // ~20s (10 * 2s) before falling through to the maxPollingCount countdown above.
  const maxTransientFailureCount = 10;

  const clearPollTimeout = () => {
    if (pollTimeoutRef.current !== undefined) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = undefined;
    }
  };

  // Returns the transient-failure count as it stood right before the reset, so callers
  // that need to report the streak that caused an error don't have to snapshot it first.
  const resetPollGeneration = () => {
    const priorTransientFailureCount = transientFailureCountRef.current;
    pollGenerationRef.current += 1;
    transientFailureCountRef.current = 0;
    return priorTransientFailureCount;
  };

  const releaseWidgetHold = () => {
    setKeepWidgetOpen(false);
  };

  const stopPolling = () => {
    resetPollGeneration();
    clearPollTimeout();
    setPollingCount(0);
    setIsLoading(false);
    setPollingRetry(false);
  };

  const handleTerminalHeartbeatError = (error: unknown) => {
    // Only 404 is terminal (heartbeat task missing). Recognized-transient codes (see
    // isTransientRefetchStatus) are retried on their own budget below instead of
    // spending pollingCount. Anything else (e.g. a permanent 401/403) falls straight
    // through to the normal pollingCount increment.
    if (extractStatusCode(error) !== 404) {
      return false;
    }

    const transientFailureCountAtFailure = resetPollGeneration();
    clearPollTimeout();
    setIsLoading(false);
    setPollingCount(0);
    setPollingRetry(false);
    setKeepWidgetOpen(false);
    setV2PollPhase('idle');
    setShowQuizResultsDelayed(false);
    setShowQuizResultsIssue(true);
    notifyQuizError(
      'Quiz results issue - heartbeat task not found (404)',
      transientFailureCountAtFailure,
    );
    return true;
  };

  // Network blips (status 0), rate limiting (429), server errors (5xx), and errors with
  // no recognizable status (a raw network failure often carries none) are treated as
  // recoverable. A *defined*, non-transient status (e.g. 401/403) is a permanent
  // failure and should spend pollingCount immediately, same as before PATHWAY-3781.
  const isTransientRefetchStatus = (error: unknown) => {
    const status = extractStatusCode(error);
    return (
      status === undefined ||
      status === 0 ||
      status === 429 ||
      (status >= 500 && status < 600)
    );
  };

  // True if a transient refetch failure still has retry budget left — and spends one
  // unit of it. Once exhausted, the caller falls through to the normal pollingCount
  // increment (and eventually the same timeout UX as sustained failure).
  const hasTransientRetryBudget = () => {
    if (transientFailureCountRef.current >= maxTransientFailureCount) {
      return false;
    }
    transientFailureCountRef.current += 1;
    return true;
  };

  const pollQuizResult = useEffectEvent(async () => {
    const generation = pollGenerationRef.current;
    if (isQuizV2 && isComplete) {
      return;
    }

    // Retries a transient failure without spending pollingCount, so a short burst of
    // network errors (status 0, 5xx, 429) can't prematurely exhaust the main poll cap.
    const retryAfterTransientFailure = () => {
      clearPollTimeout();
      pollTimeoutRef.current = setTimeout(() => {
        if (generation !== pollGenerationRef.current) {
          return;
        }
        void pollQuizResult();
      }, 2000);
    };

    // Retries on the transient budget when the failure looks recoverable and there's
    // room left; otherwise spends pollingCount like a normal cycle.
    const retryTransientOrAdvance = (error: unknown) => {
      if (isTransientRefetchStatus(error) && hasTransientRetryBudget()) {
        retryAfterTransientFailure();
        return;
      }
      setPollingCount(prevState => prevState + 1);
    };

    try {
      const result = await Promise.resolve(onRefetch());
      if (generation !== pollGenerationRef.current) {
        return;
      }

      const refetchError =
        result && typeof result === 'object' && 'error' in result
          ? result.error
          : undefined;

      if (handleTerminalHeartbeatError(refetchError)) {
        return;
      }

      if (refetchError) {
        retryTransientOrAdvance(refetchError);
      } else {
        transientFailureCountRef.current = 0;
        setPollingCount(prevState => prevState + 1);
      }
    } catch (error) {
      if (generation !== pollGenerationRef.current) {
        return;
      }

      if (handleTerminalHeartbeatError(error)) {
        return;
      }

      retryTransientOrAdvance(error);
    }
  });

  // list of status objects for current user(s)
  const userStatuses = statuses.filter(status =>
    users.find(id => status.userId === id),
  );

  // finds the status of first user taking the quiz that has not passed the quiz
  let activeQuizUserStatuses = userStatuses.filter(userStatus =>
    userStatus.steps?.find(
      stepStatus =>
        stepStatus.stepId === step.id &&
        stepStatus.result !== Constants.QUIZ_RESULTS.PASS,
    ),
  );
  if (activeQuizUserStatuses.length === 0) {
    activeQuizUserStatuses = userStatuses;
  }

  const currentUserStatus = activeQuizUserStatuses.find(
    userStatus => userStatus.userId === users[0],
  );

  const quizStatus = currentUserStatus?.steps?.find(
    stepStatus => stepStatus.stepId === step.id,
  );

  // Use `duration` (API field) not `stepDuration` (legacy normalized field).
  // See PATHWAY-2935 for context.
  const stepDuration = Math.round((quizStatus?.duration ?? 0) / 60);

  const currentStatusStep = activeQuizUserStatuses
    .at(0)
    ?.steps?.find(stepStatus => stepStatus.stepId === step.id);

  const currentStepEventId = activeQuizUserStatuses
    .at(0)
    ?.steps?.find(stepStatus => stepStatus.stepId === step.id)?.eventId;

  const notifyQuizError = (
    message: string,
    // Callers reset transientFailureCountRef right before this (heartbeat 404, polling
    // exhaustion) — always pass the pre-reset count so Datadog sees the streak that
    // actually caused the error, not the freshly-zeroed one.
    transientFailureCount: number,
  ) => {
    datadogRum.addError(new Error(message), {
      section: 'quiz',
      stepId: step.id,
      quizId: getStepQuizId(step),
      stepName: step.name,
      planId,
      users,
      pollingCount,
      transientFailureCount,
      lastPolledEventId,
      currentStepEventId,
      currentUserStatus,
      quizStatus,
      isQuizV2,
    });
  };

  const lastPolledEvent = useEffectEvent(() => {
    if (isComplete || !currentStepEventId) {
      return;
    }
    setLastPolledEventId(currentStepEventId);
  });

  useEffect(() => {
    lastPolledEvent();
    return () => {
      clearPollTimeout();
      // Invalidate the generation so a pollQuizResult already awaiting onRefetch()
      // can't schedule a retry or touch state after unmount.
      resetPollGeneration();
    };
  }, []);

  const handleQuizSubmit = useCallback(() => {
    if (!isQuizV2) {
      setIsLoading(true);
    } else {
      // Latch open before polling flips isComplete / parent clears isActive.
      setKeepWidgetOpen(true);
      setV2PollPhase('polling');
    }
    resetPollGeneration();
    setPollingCount(1);
  }, [isQuizV2]);

  // Late V2 success: close the card and drop the issue modal if polling timed out.
  useEffect(() => {
    if (!isQuizV2 || !isComplete) {
      return;
    }

    setKeepWidgetOpen(false);
    setV2PollPhase('idle');

    if (showQuizResultsIssue) {
      setShowQuizResultsIssue(false);
    }
    if (pollingCount > 0) {
      resetPollGeneration();
      clearPollTimeout();
      setPollingCount(0);
      setIsLoading(false);
      setPollingRetry(false);
    }
  }, [isQuizV2, isComplete, showQuizResultsIssue, pollingCount]);

  useEffect(() => {
    if (pollingCount > maxPollingCount) {
      // Late assignment update can land after the counter already exceeded max.
      if (isQuizV2 && isComplete) {
        stopPolling();
        return;
      }

      const transientFailureCountAtTimeout = resetPollGeneration();
      clearPollTimeout();
      setIsLoading(false);
      if (isQuizV2 || pollingRetry) {
        setKeepWidgetOpen(false);
        setV2PollPhase('idle');
        setShowQuizResultsIssue(true);
        notifyQuizError(
          isQuizV2
            ? 'Quiz results issue - polling timeout'
            : 'Quiz results issue - polling exhausted after retry',
          transientFailureCountAtTimeout,
        );
      } else {
        setShowQuizResultsDelayed(true);
        notifyQuizError(
          'Quiz results delayed - polling timeout',
          transientFailureCountAtTimeout,
        );
      }
    } else if (pollingCount) {
      if (isQuizV2 && isComplete) {
        stopPolling();
        return;
      }

      const eventIdsAreSame = currentStepEventId === lastPolledEventId;
      if (eventIdsAreSame || !currentStepEventId) {
        clearPollTimeout();
        pollTimeoutRef.current = setTimeout(() => {
          void pollQuizResult();
        }, 2000);
      } else {
        stopPolling();
        setLastPolledEventId(currentStepEventId);
        if (isQuizV2) {
          if (currentStatusStep?.result === Constants.QUIZ_RESULTS.PASS) {
            releaseWidgetHold();
            setV2PollPhase('idle');
          } else {
            setV2PollPhase('failed');
          }
        } else if (currentStatusStep?.result === Constants.QUIZ_RESULTS.PASS) {
          setActiveQuizScore(
            Math.round(
              ((currentStatusStep.score ?? 0) /
                (currentStatusStep.totalQuestions ?? 1)) *
                100,
            ),
          );
          setShowPassPopup(true);
        } else {
          setShowFailPopup(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingCount, isComplete, currentStepEventId, isQuizV2]);

  // Delayed Wait retry also needs a fresh poll generation.
  const handleDelayedWait = () => {
    setIsLoading(true);
    resetPollGeneration();
    setPollingCount(1);
    setPollingRetry(true);
    setShowQuizResultsDelayed(false);
  };
  const handlePassContinue = () => {
    onRefetch();
    setShowPassPopup(false);
  };

  const handleFailContinue = () => {
    onRefetch();
    setQuizKey(uuidv4());
    setShowFailPopup(false);
  };

  const handleRetakeQuiz = () => {
    releaseWidgetHold();
    setQuizKey(uuidv4());
    stopPolling();
    setV2PollPhase('idle');
    setShowQuizResultsIssue(false);
    setShowQuizResultsDelayed(false);
    onRefetch();
  };

  const handleClick = () => {
    // User toggled the card — drop the latch and stop any in-flight poll.
    releaseWidgetHold();
    stopPolling();
    setV2PollPhase('idle');
    setQuizKey(uuidv4());
    onClick(step);
  };

  const reloadQuiz = useCallback(() => {
    setQuizKey(uuidv4());
    setKeepWidgetOpen(true);
    clearPollTimeout();
    setV2PollPhase('idle');
    resetPollGeneration();
    setIsLoading(false);
    setPollingCount(0);
    setPollingRetry(false);
  }, []);

  const passMessageText =
    users.length === 1 && quizStatus?.score
      ? t('TrainingPlans.quizzes.quizSuccessPopup', {
          score: `${Math.round(
            (quizStatus.score / (quizStatus.totalQuestions ?? 1)) * 100,
          )}`,
        })
      : t('TrainingPlans.quizzes.quizPassed');

  const showQuizWidget = (isActive && !isComplete) || keepWidgetOpen;

  const quizId = getStepQuizId(step) ?? '';
  const stepId = step.id;
  const userIdsValue = users.toString();

  /**
   * Typeform's Widget is memoized on a deep prop compare, but functions compare by
   * identity and its internal effect re-creates the iframe whenever props change.
   * React Compiler skips this file (exhaustive-deps suppression below), so the element
   * is memoized by hand — otherwise every setPollingCount render reloads the quiz.
   */
  const quizWidget = useMemo(
    () => (
      <Widget
        className="w-full!"
        disableScroll={true}
        onEndingButtonClick={reloadQuiz}
        hidden={
          isQuizV2
            ? {
                plan_id: planId,
                step_id: stepId,
                user_ids: userIdsValue,
              }
            : {
                userIds: userIdsValue,
                checklistId: planId,
                stepId: stepId,
                language: language,
              }
        }
        hideHeaders={true}
        id={quizId}
        key={quizKey}
        onSubmit={handleQuizSubmit}
        {...(isQuizV2 ? { tracking: { 'typeform-lang': typeformLang } } : {})}
      />
    ),
    [
      isQuizV2,
      planId,
      stepId,
      userIdsValue,
      language,
      quizId,
      quizKey,
      handleQuizSubmit,
      typeformLang,
      reloadQuiz,
    ],
  );

  const activeStep = showPassPopup
    ? 'showPassPopup'
    : showFailPopup
      ? 'showFailPopup'
      : showQuizResultsDelayed
        ? 'showQuizResultsDelayed'
        : showQuizResultsIssue
          ? 'showQuizResultsIssue'
          : isLoading
            ? 'isLoading'
            : null;

  const handleModalClose = () => {
    if (activeStep === 'showPassPopup') handlePassContinue();
    else if (activeStep === 'showFailPopup') handleFailContinue();
  };

  const showCloseButton =
    activeStep === 'showPassPopup' || activeStep === 'showFailPopup';

  return (
    <>
      <div>
        <div
          className={cn(
            'flex w-full flex-row items-center gap-2',
            !disabled && 'cursor-pointer',
            showQuizWidget && 'pb-2.5',
          )}
        >
          {isComplete ? (
            <div className="mr-2">
              <IconCircleCheckDisabled />
            </div>
          ) : (
            <div className="mr-2 cursor-default">
              <IconCircleDisabled />
            </div>
          )}
          <div
            className={cn(
              'flex w-full min-w-0 flex-1 flex-col text-left',
              !disabled && 'cursor-pointer',
            )}
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={cn('text-left', !disabled && 'cursor-pointer')}
                disabled={disabled}
                onClick={handleClick}
              >
                <h3
                  data-testid="TaskTitle"
                  className="text-variant-overline3 text-grayscale-gray6!"
                >
                  {t('Generic.quiz')}
                  {isComplete &&
                    ` - ${t('Generic.completedIn')} ${stepDuration} ${t(
                      'Generic.mins',
                    )}`}
                </h3>
              </button>
            </div>
            <button
              type="button"
              className={cn('text-left', !disabled && 'cursor-pointer')}
              disabled={disabled}
              onClick={handleClick}
            >
              <p data-testid="QuizName" className="text-variant-body1">
                {itemByLanguage(step.name, '', language)}
              </p>
            </button>
          </div>
          <button
            type="button"
            className={cn(
              'text-grayscale-gray2 mx-1.5 flex w-6',
              !disabled && 'cursor-pointer',
            )}
            disabled={disabled}
            onClick={handleClick}
          >
            <IconFileCertificate className="size-6!" />
          </button>
        </div>
        {isComplete && (
          <p
            data-testid="PassMessage"
            className="text-variant-body1 text-feedback-success-base! pl-10!"
          >
            {passMessageText}
          </p>
        )}
      </div>
      {showQuizWidget && (
        <div className="relative flex h-[500px] w-full">
          {quizWidget}
          {isQuizV2 && v2PollPhase === 'polling' && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/30"
              data-testid="v2-poll-overlay"
            >
              <LoadingIndicator data-testid="v2-poll-spinner" size="md" />
            </div>
          )}
        </div>
      )}
      <PathwayModal
        className={cn('2xl:max-w-[610px]!', !showCloseButton && 'pt-8!')}
        isOpen={activeStep !== null}
        onClose={showCloseButton ? handleModalClose : () => {}}
        overlayStyle={{ zIndex: 10003 }}
        showCloseButton={showCloseButton}
        stepKey={activeStep ?? ''}
      >
        {activeStep === 'showPassPopup' && (
          <ConfirmationModal
            isStep
            bodyText={t('TrainingPlans.quizzes.quizSuccessPopup', {
              score: activeQuizScore,
            })}
            bodyTextColor="success"
            headerText={t('TrainingPlans.quizzes.congratulations')}
            primaryButtonHandler={handlePassContinue}
            primaryButtonText={t('Button.continue')}
          />
        )}
        {activeStep === 'showFailPopup' && (
          <ConfirmationModal
            isStep
            bodyText={t('TrainingPlans.quizzes.quizFail')}
            headerText={t('TrainingPlans.quizzes.youDidNotPass')}
            primaryButtonHandler={handleFailContinue}
            primaryButtonText={t('Button.continue')}
          />
        )}
        {activeStep === 'showQuizResultsDelayed' && (
          <ConfirmationModal
            isStep
            bodyText={t('TrainingPlans.quizzes.quizResultsDelayed')}
            headerText={t('TrainingPlans.quizzes.calculatingQuiz')}
            primaryButtonHandler={handleRetakeQuiz}
            primaryButtonText={t('Button.retake')}
            secondaryButtonHandler={handleDelayedWait}
            secondaryButtonText={t('Button.wait')}
          />
        )}
        {activeStep === 'showQuizResultsIssue' && (
          <ConfirmationModal
            isStep
            bodyText={t('TrainingPlans.quizzes.quizResultsIssue')}
            headerText={t('TrainingPlans.quizzes.calculatingQuiz')}
            primaryButtonHandler={handleRetakeQuiz}
            primaryButtonText={t('Button.retake')}
          />
        )}
        {activeStep === 'isLoading' && (
          <PathwayModal.Body className="py-12!">
            <h3 className="text-variant-h3">
              {t('TrainingPlans.quizzes.quizWaitingForResults')}
            </h3>
          </PathwayModal.Body>
        )}
      </PathwayModal>
    </>
  );
};

// memo to prevent re-renders when taking the quiz
export default memo(PlanItemQuiz);
